package data.datasource.remote

import data.graphql.GitHubGraphQLQueries
import domain.repository.AuthenticationRepository
import domain.model.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.delay
import kotlinx.serialization.Serializable
import kotlinx.serialization.Contextual
import kotlinx.serialization.json.*

/**
 * GitHub App Proxy Data Source
 * 
 * This data source uses the GitHub App proxy endpoints with OAuth session authentication.
 * Benefits:
 * - OAuth authentication for personalized UI
 * - GitHub App tokens for private repo access
 * - Never exposes secrets to the browser
 * - Session-based authentication with server-side token management
 */
class GitHubAppProxyDataSource(
    private val httpClient: HttpClient,
    private val authRepository: AuthenticationRepository,
    private val baseUrl: String = ""
) {
    private val graphqlProxyEndpoint = "$baseUrl/api/github/graphql"
    private val installationsEndpoint = "$baseUrl/api/github/installations"
    private val userInfoEndpoint = "$baseUrl/api/auth/user"
    
    /**
     * Execute GraphQL query via GitHub App proxy with session authentication
     */
    private suspend fun executeProxyGraphQL(
        query: String,
        installationId: Int,
        variables: Map<String, String> = emptyMap()
    ): GraphQLProxyResponse {
        try {
            val requestBody = GraphQLProxyRequest(
                query = query,
                variables = variables,
                installationId = installationId
            )
            
            val response = httpClient.request {
                url(graphqlProxyEndpoint)
                method = HttpMethod.Post
                contentType(ContentType.Application.Json)
                setBody(requestBody)
                // Cookies with session ID are automatically included
            }
            
            if (response.status.isSuccess()) {
                return response.body<GraphQLProxyResponse>()
            } else {
                throw Exception("GitHub App proxy request failed with status: ${response.status}")
            }
        } catch (e: Exception) {
            println("🚫 GitHub App Proxy Error: ${e.message}")
            throw GitHubAppProxyException("Failed to execute GraphQL query via proxy", e)
        }
    }
    
    /**
     * Get current user info and their accessible installations
     */
    suspend fun getUserInfo(): UserInfo? {
        try {
            val response = httpClient.request {
                url(userInfoEndpoint)
                method = HttpMethod.Get
                // Session cookie automatically included
            }
            
            if (response.status.isSuccess()) {
                return response.body<UserInfo>()
            } else if (response.status == HttpStatusCode.Unauthorized) {
                return null // User not authenticated
            } else {
                throw Exception("Failed to fetch user info with status: ${response.status}")
            }
        } catch (e: Exception) {
            println("🚫 Error fetching user info: ${e.message}")
            return null
        }
    }
    
    /**
     * Get available GitHub App installations that user can access
     */
    suspend fun getAvailableInstallations(): List<GitHubAppInstallation> {
        try {
            val response = httpClient.request {
                url(installationsEndpoint)
                method = HttpMethod.Get
                // Session cookie automatically included
            }
            
            if (response.status.isSuccess()) {
                val installationsResponse = response.body<InstallationsResponse>()
                return installationsResponse.installations
            } else {
                throw Exception("Failed to fetch installations with status: ${response.status}")
            }
        } catch (e: Exception) {
            println("🚫 Error fetching GitHub App installations: ${e.message}")
            throw GitHubAppProxyException("Failed to fetch installations", e)
        }
    }
    
    /**
     * Get user's organizations via GitHub App proxy
     */
    suspend fun getUserOrganizations(installationId: Int): List<OrganizationInsights> {
        val organizations = mutableListOf<OrganizationInsights>()
        var cursor: String? = null
        var hasNextPage = true
        
        while (hasNextPage) {
            try {
                val variables = mapOf(
                    "cursor" to (cursor ?: "")
                )
                
                val response = executeProxyGraphQL(
                    GitHubGraphQLQueries.GET_USER_ORGANIZATIONS,
                    installationId,
                    variables
                )
                
                val data = response.data?.jsonObject
                val viewer = data?.get("viewer")?.jsonObject
                val orgsConnection = viewer?.get("organizations")?.jsonObject
                val edges = orgsConnection?.get("edges")?.jsonArray
                
                edges?.forEach { edge ->
                    val edgeObj = edge.jsonObject
                    val orgNode = edgeObj["node"]?.jsonObject
                    
                    if (orgNode != null) {
                        val orgInsights = parseOrganizationInsights(orgNode, null)
                        organizations.add(orgInsights)
                    }
                }
                
                val pageInfo = orgsConnection?.get("pageInfo")?.jsonObject
                hasNextPage = pageInfo?.get("hasNextPage")?.jsonPrimitive?.boolean ?: false
                cursor = pageInfo?.get("endCursor")?.jsonPrimitive?.contentOrNull
                
            } catch (e: Exception) {
                println("🚫 Error fetching user organizations via proxy: ${e.message}")
                break
            }
        }
        
        return organizations
    }
    
    /**
     * Diagnose organization access issues via GitHub App proxy
     */
    suspend fun diagnoseOrganizationAccess(installationId: Int): OrganizationAccessDiagnosis {
        try {
            val response = executeProxyGraphQL(
                GitHubGraphQLQueries.GET_USER_ORGANIZATION_MEMBERSHIPS,
                installationId
            )
            
            val data = response.data?.jsonObject
            val viewer = data?.get("viewer")?.jsonObject
            
            // Get basic user info
            val userLogin = viewer?.get("login")?.jsonPrimitive?.content ?: "unknown"
            val verifiedEmails = viewer?.get("organizationVerifiedDomainEmails")?.jsonArray?.map { 
                it.jsonPrimitive.content 
            } ?: emptyList()
            
            // Get visible organizations for this installation
            val visibleOrgs = getUserOrganizations(installationId)
            
            return OrganizationAccessDiagnosis(
                userLogin = userLogin,
                visibleOrganizationCount = visibleOrgs.size,
                visibleOrganizations = visibleOrgs.map { it.organization.login },
                verifiedDomainEmails = verifiedEmails,
                recommendations = generateAppAccessRecommendations(visibleOrgs.size, verifiedEmails)
            )
            
        } catch (e: Exception) {
            println("🚫 Error diagnosing organization access via proxy: ${e.message}")
            return OrganizationAccessDiagnosis(
                userLogin = "unknown",
                visibleOrganizationCount = 0,
                visibleOrganizations = emptyList(),
                verifiedDomainEmails = emptyList(),
                recommendations = listOf("Error occurred during diagnosis. Please check GitHub App installation.")
            )
        }
    }
    
    /**
     * Get pull requests with enhanced insights via GitHub App proxy
     */
    suspend fun getPullRequestsInsights(
        installationId: Int,
        searchQuery: String,
        maxResults: Int = 100
    ): List<PullRequestInsights> {
        val pullRequests = mutableListOf<PullRequestInsights>()
        var cursor: String? = null
        var hasNextPage = true
        var fetchedCount = 0
        
        while (hasNextPage && fetchedCount < maxResults) {
            try {
                val variables = mapOf(
                    "query" to searchQuery,
                    "cursor" to (cursor ?: "")
                )
                
                val response = executeProxyGraphQL(
                    GitHubGraphQLQueries.GET_PULL_REQUESTS_INSIGHTS,
                    installationId,
                    variables
                )
                
                val data = response.data?.jsonObject
                val search = data?.get("search")?.jsonObject
                val edges = search?.get("edges")?.jsonArray
                
                edges?.forEach { edge ->
                    val node = edge.jsonObject["node"]?.jsonObject
                    if (node != null && fetchedCount < maxResults) {
                        val prInsights = parsePullRequestInsights(node)
                        pullRequests.add(prInsights)
                        fetchedCount++
                    }
                }
                
                val pageInfo = search?.get("pageInfo")?.jsonObject
                hasNextPage = pageInfo?.get("hasNextPage")?.jsonPrimitive?.boolean ?: false
                cursor = pageInfo?.get("endCursor")?.jsonPrimitive?.contentOrNull
                
                // Add small delay to avoid rate limiting
                if (hasNextPage) {
                    delay(100)
                }
                
            } catch (e: Exception) {
                println("🚫 Error fetching PR insights via proxy: ${e.message}")
                break
            }
        }
        
        return pullRequests
    }
    
    /**
     * Get detailed organization information via GitHub App proxy
     */
    suspend fun getOrganizationDetails(installationId: Int, login: String): OrganizationInsights? {
        try {
            val variables = mapOf(
                "login" to login
            )
            
            val response = executeProxyGraphQL(
                GitHubGraphQLQueries.GET_ORGANIZATION_DETAILS,
                installationId,
                variables
            )
            
            val data = response.data?.jsonObject
            val organization = data?.get("organization")?.jsonObject
            
            return if (organization != null) {
                parseOrganizationInsights(organization, null)
            } else {
                null
            }
        } catch (e: Exception) {
            println("🚫 Error fetching organization details for $login via proxy: ${e.message}")
            return null
        }
    }
    
    private fun generateAppAccessRecommendations(visibleOrgCount: Int, verifiedEmails: List<String>): List<String> {
        val recommendations = mutableListOf<String>()
        
        if (visibleOrgCount < 2) {
            recommendations.add("🔐 GitHub App shows $visibleOrgCount organization(s) for this installation.")
            recommendations.add("📋 The GitHub App may need to be installed on additional organizations")
            recommendations.add("🏢 Ask organization admins to install the GitHub App on their organizations")
            recommendations.add("⚙️ Check if the GitHub App has the correct permissions: Repository (read), Organization (read)")
        }
        
        if (verifiedEmails.isNotEmpty()) {
            recommendations.add("✅ You have verified domain emails: ${verifiedEmails.joinToString(", ")}")
            recommendations.add("📧 Organizations matching these domains may require SAML SSO completion")
        }
        
        recommendations.add("🔄 Ensure the GitHub App is installed on all organizations you need access to")
        recommendations.add("🎯 GitHub Apps provide better security than OAuth tokens")
        recommendations.add("💡 Contact your organization admin to install the app if it's missing")
        
        return recommendations
    }
    
    // Parsing methods (reused from original data source)
    private fun parseOrganizationInsights(orgJson: JsonObject, role: String?): OrganizationInsights {
        val organization = GitHubOrganization(
            id = orgJson["id"]?.jsonPrimitive?.content ?: "",
            login = orgJson["login"]?.jsonPrimitive?.content ?: "",
            name = orgJson["name"]?.jsonPrimitive?.contentOrNull,
            description = orgJson["description"]?.jsonPrimitive?.contentOrNull,
            avatarUrl = orgJson["avatarUrl"]?.jsonPrimitive?.content ?: "",
            htmlUrl = orgJson["url"]?.jsonPrimitive?.content ?: "",
            location = orgJson["location"]?.jsonPrimitive?.contentOrNull,
            email = orgJson["email"]?.jsonPrimitive?.contentOrNull,
            twitterUsername = orgJson["twitterUsername"]?.jsonPrimitive?.contentOrNull,
            blog = orgJson["websiteUrl"]?.jsonPrimitive?.contentOrNull,
            company = null,
            createdAt = orgJson["createdAt"]?.jsonPrimitive?.content ?: "",
            updatedAt = orgJson["updatedAt"]?.jsonPrimitive?.content ?: "",
            publicRepos = orgJson["repositories"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            privateRepos = orgJson["repositories"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            totalPrivateRepos = orgJson["repositories"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            publicGists = 0,
            followers = orgJson["membersWithRole"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            following = 0,
            type = "Organization",
            membership = role?.let { 
                OrganizationMembership(
                    role = it,
                    state = "active",
                    url = "",
                    organizationUrl = orgJson["url"]?.jsonPrimitive?.content ?: "",
                    permissions = MembershipPermissions(
                        canCreateRepository = true,
                        canCreatePrivateRepository = true,
                        canCreatePublicRepository = true,
                        canCreateTeam = true
                    )
                )
            },
            ssoProvider = orgJson["samlIdentityProvider"]?.jsonObject?.let { saml ->
                SSOProvider(
                    enabled = true,
                    provider = "saml",
                    issuer = null,
                    loginUrl = saml["ssoUrl"]?.jsonPrimitive?.contentOrNull,
                    certificateFingerprint = null
                )
            },
            isVerified = orgJson["isVerified"]?.jsonPrimitive?.boolean ?: false,
            requiresSAML = false,
            pendingInvitations = emptyList()
        )
        
        val teams = orgJson["teams"]?.jsonObject?.get("nodes")?.jsonArray?.map { teamNode ->
            parseTeam(teamNode.jsonObject)
        } ?: emptyList()
        
        val activity = OrganizationActivity(
            totalPullRequests = 0,
            openPullRequests = 0,
            totalIssues = 0,
            openIssues = 0,
            lastActivityAt = null
        )
        
        val accessLevel = when (role?.uppercase()) {
            "OWNER" -> OrganizationAccessLevel.OWNER
            "ADMIN" -> OrganizationAccessLevel.ADMIN
            "MEMBER" -> OrganizationAccessLevel.MEMBER
            else -> OrganizationAccessLevel.NONE
        }
        
        return OrganizationInsights(
            organization = organization,
            ssoStatus = null,
            teams = teams,
            recentActivity = activity,
            accessLevel = accessLevel
        )
    }
    
    private fun parsePullRequestInsights(prJson: JsonObject): PullRequestInsights {
        val repository = Repository(
            id = prJson["repository"]?.jsonObject?.get("id")?.jsonPrimitive?.content ?: "",
            name = prJson["repository"]?.jsonObject?.get("name")?.jsonPrimitive?.content ?: "",
            nameWithOwner = prJson["repository"]?.jsonObject?.get("nameWithOwner")?.jsonPrimitive?.content ?: "",
            htmlUrl = prJson["repository"]?.jsonObject?.get("url")?.jsonPrimitive?.content ?: "",
            isPrivate = prJson["repository"]?.jsonObject?.get("isPrivate")?.jsonPrimitive?.boolean ?: false,
            primaryLanguage = prJson["repository"]?.jsonObject?.get("primaryLanguage")?.jsonObject?.get("name")?.jsonPrimitive?.contentOrNull,
            organization = prJson["repository"]?.jsonObject?.get("owner")?.jsonObject?.let { owner ->
                RepositoryOrganization(
                    login = owner["login"]?.jsonPrimitive?.content ?: "",
                    name = owner["name"]?.jsonPrimitive?.contentOrNull,
                    avatarUrl = owner["avatarUrl"]?.jsonPrimitive?.content ?: ""
                )
            }
        )
        
        return PullRequestInsights(
            id = prJson["id"]?.jsonPrimitive?.content ?: "",
            number = prJson["number"]?.jsonPrimitive?.int ?: 0,
            title = prJson["title"]?.jsonPrimitive?.content ?: "",
            htmlUrl = prJson["url"]?.jsonPrimitive?.content ?: "",
            state = prJson["state"]?.jsonPrimitive?.content ?: "",
            isDraft = prJson["isDraft"]?.jsonPrimitive?.boolean ?: false,
            createdAt = prJson["createdAt"]?.jsonPrimitive?.content ?: "",
            updatedAt = prJson["updatedAt"]?.jsonPrimitive?.content ?: "",
            author = parseAuthor(prJson["author"]?.jsonObject),
            repository = repository,
            labels = prJson["labels"]?.jsonObject?.get("nodes")?.jsonArray?.map { labelNode ->
                parseLabel(labelNode.jsonObject)
            } ?: emptyList(),
            reviewDecision = prJson["reviewDecision"]?.jsonPrimitive?.contentOrNull,
            totalCommentsCount = prJson["comments"]?.jsonObject?.get("totalCount")?.jsonPrimitive?.int ?: 0,
            additions = prJson["additions"]?.jsonPrimitive?.int ?: 0,
            deletions = prJson["deletions"]?.jsonPrimitive?.int ?: 0,
            changedFiles = prJson["changedFiles"]?.jsonPrimitive?.int ?: 0,
            mergeable = prJson["mergeable"]?.jsonPrimitive?.contentOrNull,
            baseRefName = prJson["baseRefName"]?.jsonPrimitive?.content ?: "",
            headRefName = prJson["headRefName"]?.jsonPrimitive?.content ?: "",
            organization = repository.organization?.let { orgRepo ->
                null // Placeholder for now
            },
            teams = emptyList(),
            assignees = prJson["assignees"]?.jsonObject?.get("nodes")?.jsonArray?.map { assigneeNode ->
                parseAuthor(assigneeNode.jsonObject)
            } ?: emptyList(),
            reviewRequests = prJson["reviewRequests"]?.jsonObject?.get("nodes")?.jsonArray?.map { requestNode ->
                ReviewRequest(
                    requestedReviewer = parseAuthor(requestNode.jsonObject["requestedReviewer"]?.jsonObject)
                )
            } ?: emptyList(),
            reviews = prJson["reviews"]?.jsonObject?.get("nodes")?.jsonArray?.map { reviewNode ->
                parseReview(reviewNode.jsonObject)
            } ?: emptyList()
        )
    }
    
    private fun parseAuthor(authorJson: JsonObject?): Author {
        return Author(
            login = authorJson?.get("login")?.jsonPrimitive?.content ?: "",
            name = authorJson?.get("name")?.jsonPrimitive?.contentOrNull,
            avatarUrl = authorJson?.get("avatarUrl")?.jsonPrimitive?.content ?: "",
            htmlUrl = authorJson?.get("url")?.jsonPrimitive?.content ?: ""
        )
    }
    
    private fun parseLabel(labelJson: JsonObject): Label {
        return Label(
            name = labelJson["name"]?.jsonPrimitive?.content ?: "",
            color = labelJson["color"]?.jsonPrimitive?.content ?: "",
            description = labelJson["description"]?.jsonPrimitive?.contentOrNull
        )
    }
    
    private fun parseTeam(teamJson: JsonObject): Team {
        return Team(
            id = teamJson["id"]?.jsonPrimitive?.content ?: "",
            name = teamJson["name"]?.jsonPrimitive?.content ?: "",
            slug = teamJson["slug"]?.jsonPrimitive?.content ?: "",
            description = teamJson["description"]?.jsonPrimitive?.contentOrNull,
            privacy = teamJson["privacy"]?.jsonPrimitive?.content ?: "",
            organization = TeamOrganization(
                login = teamJson["organization"]?.jsonObject?.get("login")?.jsonPrimitive?.content ?: "",
                name = teamJson["organization"]?.jsonObject?.get("name")?.jsonPrimitive?.contentOrNull
            )
        )
    }
    
    private fun parseReview(reviewJson: JsonObject): Review {
        return Review(
            id = reviewJson["id"]?.jsonPrimitive?.content ?: "",
            author = parseAuthor(reviewJson["author"]?.jsonObject),
            state = reviewJson["state"]?.jsonPrimitive?.content ?: "",
            submittedAt = reviewJson["submittedAt"]?.jsonPrimitive?.contentOrNull
        )
    }
}

// Data classes for GitHub App proxy communication
@Serializable
data class GraphQLProxyRequest(
    val query: String,
    val variables: Map<String, String> = emptyMap(),
    val installationId: Int
)

@Serializable
data class GraphQLProxyResponse(
    val data: JsonElement? = null,
    val errors: List<GraphQLError>? = null,
    val installationId: Int,
    val user: String? = null,
    val timestamp: String
)

@Serializable
data class UserInfo(
    val authenticated: Boolean,
    val user: UserDetails? = null,
    val installations: List<GitHubAppInstallation> = emptyList(),
    val sessionExpiresAt: Long? = null
)

@Serializable
data class UserDetails(
    val id: Int,
    val login: String,
    val email: String? = null,
    val name: String? = null,
    val avatar_url: String
)

@Serializable
data class GitHubAppInstallation(
    val id: Int,
    val account: InstallationAccount,
    val repository_selection: String,
    val permissions: JsonObject,
    val created_at: String,
    val updated_at: String,
    val suspended: Boolean = false
)

@Serializable
data class InstallationAccount(
    val login: String,
    val id: Int,
    val type: String,
    val avatar_url: String
)

@Serializable
data class InstallationsResponse(
    val installations: List<GitHubAppInstallation>,
    val total_count: Int,
    val filtered_by_user: Boolean,
    val timestamp: String
)

@Serializable
data class GraphQLError(
    val message: String,
    val path: List<String>? = null
)

class GitHubAppProxyException(message: String, cause: Throwable? = null) : Exception(message, cause)